import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgLevel } from '@/api/orgAdmin';

/**
 * The body of `POST /organizations/{id}/assignments` — (user_sub, optional email,
 * level). Identical on both screens because it is literally the same request body
 * (`AssignmentCreateSerializer`), so it is one shape rather than two.
 */
export interface AssignLevelFormModel {
    user_sub: string;
    user_email: string;
    level_id: string;
}

export interface AssignLevelFormProps {
    /** Lets a `Modal` footer button submit this form via its own `form="…"` attribute. */
    formId: string;
    value: AssignLevelFormModel;
    onChange: (next: AssignLevelFormModel) => void;
    /** Active levels of the org this grant is being written to. */
    levels: OrgLevel[];
    error?: string | null;
    onSubmit: (event: FormEvent) => void;
}

/**
 * EAA-1839: the shared "cấp Level cho một tài khoản đã có" form.
 *
 * WHY THIS EXISTS. Two screens now write the same single endpoint: the tree's
 * "Cấp quyền" modal (`LevelAssignmentModals`, EAA-1818) and the scoped accounts
 * browser's "+ Tài khoản" (`scopedAccounts/ScopedAccountDialogs`). The second one
 * initially re-declared the three fields, which is exactly the duplication this
 * ticket's brief forbids — "do not build a parallel one if a reusable pattern
 * exists". The existing modal could not simply be reused whole, because it is
 * bound to `HierarchyPageVM` and lifting that view model is out of this ticket's
 * scope; what IS liftable is the form body, which owns no state at all. So this is
 * a presentational component: both screens keep their own mutation, their own
 * error string and their own footer, and share the fields, copy and validation
 * surface. One place to change when the serializer changes.
 *
 * Stays in `hierarchy/` rather than `components/ui/`: it names domain vocabulary
 * (`user_sub`, Level) and is specific to this endpoint, so it is page-local by the
 * repo's shared-vs-local rule.
 *
 * The fields are wrapped in real `<label>`s. The tree's modal previously used bare
 * placeholder-only inputs; unifying on labelled fields is a deliberate accessibility
 * fix (a placeholder is not an accessible name and disappears on input), not an
 * incidental restyle.
 */
export function AssignLevelForm({
    formId,
    value,
    onChange,
    levels,
    error,
    onSubmit,
}: AssignLevelFormProps) {
    const { t } = useTranslation();
    return (
        <form id={formId} className="portal-admin-form" onSubmit={onSubmit}>
            <label>
                {t('console.hierarchy.assignLevelForm.userSubLabel')}
                <input
                    value={value.user_sub}
                    onChange={(event) => onChange({ ...value, user_sub: event.target.value })}
                    placeholder={t('console.hierarchy.assignLevelForm.userSubPlaceholder')}
                    required
                />
            </label>
            <label>
                {t('console.hierarchy.assignLevelForm.emailLabel')}
                <input
                    type="email"
                    value={value.user_email}
                    onChange={(event) => onChange({ ...value, user_email: event.target.value })}
                    placeholder={t('console.hierarchy.assignLevelForm.emailPlaceholder')}
                />
            </label>
            <label>
                {t('console.hierarchy.assignLevelForm.levelLabel')}
                <select
                    value={value.level_id}
                    onChange={(event) => onChange({ ...value, level_id: event.target.value })}
                >
                    <option value="">
                        {t('console.hierarchy.assignLevelForm.levelPlaceholder')}
                    </option>
                    {levels.map((level) => (
                        <option key={level.id} value={level.id}>
                            {t('console.hierarchy.assignLevelForm.levelOptionLabel', {
                                name: level.name,
                                roleKey: level.role_key,
                            })}
                        </option>
                    ))}
                </select>
            </label>
            {levels.length === 0 && (
                <p className="portal-admin-hint">
                    {t('console.hierarchy.assignLevelForm.noLevelsHint')}
                </p>
            )}
            {error && <p className="portal-admin-error">{error}</p>}
        </form>
    );
}
